/**
 * Pending-audit-id sessionStorage helpers.
 *
 * Used by the SMS conversion-funnel deep link (`/start?audit=<id>`) to carry
 * the audit id through the signup flow. `/start` captures the query param
 * via `setPendingAuditId`; `/create-workspace` reads + clears it via
 * `consumePendingAuditId` after a successful workspace claim and routes
 * to the canonical `/i/<id>` resolver (commit 643249da).
 *
 * Mirrors the existing `startVerifiedPhoneNumber` sessionStorage pattern
 * (`pages/start.vue` ↔ `pages/create-account.vue`) but extracted into a
 * unit-testable util because page-mount tests for full Quasar pages are
 * not viable with the current vitest setup.
 */

export const PENDING_AUDIT_STORAGE_KEY = 'startPendingAuditId';

type AuditIdInput = string | string[] | null | undefined;

const isBrowser = (): boolean => typeof window !== 'undefined' && !!window.sessionStorage;

const normalizeAuditId = (raw: AuditIdInput): string | null => {
  if (raw == null) {
    return null;
  }
  const candidate = Array.isArray(raw) ? raw[0] : raw;
  if (typeof candidate !== 'string') {
    return null;
  }
  const trimmed = candidate.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Persist a pending audit id to sessionStorage. Empty / nullish / array-empty
 * inputs clear any existing key. SSR-safe (no-op when window is undefined).
 */
export const setPendingAuditId = (auditId: AuditIdInput): void => {
  if (!isBrowser()) {
    return;
  }
  const normalized = normalizeAuditId(auditId);
  if (normalized) {
    window.sessionStorage.setItem(PENDING_AUDIT_STORAGE_KEY, normalized);
  } else {
    window.sessionStorage.removeItem(PENDING_AUDIT_STORAGE_KEY);
  }
};

/**
 * Read and clear the pending audit id. Returns null when absent, when the
 * stored value is empty/whitespace, or when called during SSR.
 */
export const consumePendingAuditId = (): string | null => {
  if (!isBrowser()) {
    return null;
  }
  const raw = window.sessionStorage.getItem(PENDING_AUDIT_STORAGE_KEY);
  window.sessionStorage.removeItem(PENDING_AUDIT_STORAGE_KEY);
  if (!raw) {
    return null;
  }
  const trimmed = raw.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Remove the pending audit id without reading it. SSR-safe.
 */
export const clearPendingAuditId = (): void => {
  if (!isBrowser()) {
    return;
  }
  window.sessionStorage.removeItem(PENDING_AUDIT_STORAGE_KEY);
};

/**
 * Build the audit deep-link path for the SMS conversion funnel.
 *
 * The post id from the SMS deep-link (`pocpost_*`) lives in the `posts`
 * table, NOT the `items` table — so the `/i/<id>` resolver (introduced
 * in commit 643249da, ItemResolverService) returns 404 because it looks
 * up `prisma.item`. The posts-aware page is `pages/audit/[postId].vue`,
 * served at `/audit/<postId>`. Use that route here so the SMS click
 * resolves cleanly post-claim.
 */
export const buildAuditPath = (auditId: string): string => {
  const trimmed = auditId.trim();
  return `/audit/${encodeURIComponent(trimmed)}`;
};
